import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UsersRound, Landmark, Trophy, ArrowRight, Play, Shield, Globe2 } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { WSBFWordmark } from "../components/WSBFWordmark";
import { NoBetsStrip } from "../components/NoBetsStrip";

const IMG = {
  hero: "/assets/home/hero/wstf-hero.webp",
  rankingsBg: "/assets/home/rankings_bg.jpg",
  worldcup: "/assets/home/competitions/world-cup.webp",
  europe: "/assets/home/competitions/europe.webp",
  strike5: "/assets/home/competitions/strike5.webp",
  arenabook: "/assets/home/competitions/arenabook.webp",
  challenge: "/assets/home/competitions/challenge.webp",
};

const PORTRAITS = [
  "/assets/home/players/player-01.webp",
  "/assets/home/players/player-02.webp",
  "/assets/home/players/player-03.webp",
  "/assets/home/players/player-04.webp",
  "/assets/home/players/player-05.webp",
  "/assets/home/players/player-06.webp",
  "/assets/home/players/player-07.webp",
  "/assets/home/players/player-08.webp",
  "/assets/home/players/player-09.webp",
  "/assets/home/players/player-10.webp",
  "/assets/home/players/player-11.webp",
  "/assets/home/players/player-12.webp",
];

// The whole triad crossfades together through disjoint groups of 3 ranks.
const ROTATE_INTERVAL = 30000; // ms the group stays before switching
const ROTATE_FADE = 800;      // ms crossfade duration (image + overlay together)

const COMPETITIONS = [
  { to: "/world-championship", title: "World Cup", sub: "The Ultimate Stage", img: IMG.worldcup, ratio: "6 / 5" },
  { to: "/continental", title: "EUROPE", sub: "Champions Cup", img: IMG.europe, ratio: "6 / 5" },
  { to: "/strike5", title: "STRIKE 5", sub: "Global Tournament", img: IMG.strike5, ratio: "6 / 5" },
  { to: "/challenge", title: "Challenge", sub: "Live Competitions", img: IMG.challenge, ratio: "6 / 5" },
  { to: "/competitions", title: "ARENABOOK", sub: "The Prediction Arena", img: IMG.arenabook, ratio: "6 / 5" },
];

// Shared layout container — approved expansive full-width.
const SHELL = "max-w-[2048px] mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24";

export default function Home() {
  const [stats, setStats] = useState({ players: 0, clubs: 0, feds: 0, comps: 0 });
  const [topPlayers, setTopPlayers] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, c, f, cm] = await Promise.all([
          api.get("/players?limit=1"),
          api.get("/clubs"),
          api.get("/federations"),
          api.get("/competitions"),
        ]);
        setStats({
          players: p.data.total || 0,
          clubs: (c.data.clubs || []).length,
          feds: (f.data.federations || []).length,
          comps: (cm.data.competitions || []).length,
        });
      } catch { /* leave zeros */ }
    })();
    api.get("/rankings?scope=players_world&limit=12")
      .then(({ data }) => setTopPlayers(data.items || []))
      .catch(() => setTopPlayers([]));
  }, []);

  return (
    <div data-testid="home-page" className="bg-[var(--wsbf-black)] text-slate-100">
      {/* ============ HERO ============ */}
      {/* Full-width key-art. The image carries ALL text/CTAs baked in, so no HTML overlays here
          except one invisible hotspot over the gold "JOIN WSTF" button. Image is never cropped:
          object-contain, width 100%, height auto, natural 1931:814 aspect preserved at every size. */}
      <section className="w-full bg-[#000]" data-testid="home-hero">
        <div className="relative w-full">
          <img
            src={IMG.hero}
            alt="WSTF — Players. Nations. A Global Stage."
            className="block w-full h-auto object-contain select-none"
            data-testid="hero-keyart"
          />
          {/* Invisible clickable hotspot over the baked-in "JOIN WSTF" gold button.
              Percentage-based so it scales with the image at all viewports. */}
          <Link
            to="/register"
            aria-label="Join WSTF"
            data-testid="hero-join-hotspot"
            className="absolute"
            style={{
              left: "4%",
              top: "67%",
              width: "13%",
              height: "10%",
              cursor: "pointer",
            }}
          />
        </div>
      </section>

      <NoBetsStrip />

      {/* ============ COUNTERS STRIP ============ */}
      <section
        className="relative bg-[#000] border-t border-[rgba(255,215,0,0.22)]"
        data-testid="home-counters-section"
      >
        <div className={`${SHELL} py-5 lg:py-6`}>
          <div
            className="flex flex-wrap items-center gap-y-3 animate-fade-up"
            data-testid="home-counters"
          >
            <Counter Icon={Users} value={stats.players.toLocaleString()} label="Players" testid="counter-players" />
            <Divider />
            <Counter Icon={UsersRound} value={stats.clubs} label="Clubs" testid="counter-clubs" />
            <Divider />
            <Counter Icon={Landmark} value={stats.feds} label="Federations" testid="counter-federations" />
            <Divider />
            <Counter Icon={Trophy} value={stats.comps} label="Global Competitions" testid="counter-competitions" />
          </div>
        </div>
      </section>

      {/* ============ GLOBAL RANKINGS ============ */}
      <section
        className="relative overflow-hidden bg-[#000] border-t border-[rgba(255,215,0,0.28)]"
        data-testid="home-top3"
      >
        <img src={IMG.rankingsBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.22] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000] via-[rgba(0,0,0,0.90)] to-[rgba(0,0,0,0.65)] pointer-events-none" />
        <div className={`relative ${SHELL} py-8 lg:py-14 grid lg:grid-cols-12 gap-10 items-center`}>
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.38em] font-bold mb-4 wsbf-gold-text">Global Rankings</div>
            <h2 className="font-heading leading-[0.88]" data-testid="rankings-title">
              <span className="block wsbf-slant-block text-2xl sm:text-3xl lg:text-4xl text-slate-50 wsbf-glow">The World's</span>
              <span className="block wsbf-slant-block text-3xl sm:text-4xl lg:text-5xl wsbf-gold-text wsbf-glow-strong">Top Players</span>
            </h2>
            <p className="mt-5 text-xs sm:text-sm uppercase tracking-[0.26em] text-slate-200/90 font-semibold">
              Real Skill. Real Rankings. Worldwide.
            </p>
            <Link
              to="/rankings"
              data-testid="top3-full-rankings"
              className="group mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-sm text-xs font-bold uppercase tracking-wider text-slate-100 wsbf-foil hover:text-[var(--wsbf-gold-300)]"
              style={{ transition: "color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 36px rgba(212,175,55,0.25)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; }}
            >
              View Full Rankings
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1" style={{ transition: "transform 0.2s ease" }} />
            </Link>
          </div>

          <div className="lg:col-span-6">
            {topPlayers === null ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => <div key={i} className="aspect-[5/6] rounded-lg bg-[var(--wsbf-black-2)] wsbf-foil animate-pulse" />)}
              </div>
            ) : topPlayers.length === 0 ? (
              <div className="text-center py-16 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-lg">
                No ranked players yet
              </div>
            ) : (
              <TopPlayersTriad players={topPlayers} />
            )}
          </div>

          <div className="hidden lg:block lg:col-span-2">
            <div className="font-heading leading-tight tracking-wide text-base wsbf-gold-text wsbf-slant-block">
              <div>Players</div>
              <div>From Every Nation.</div>
              <div>Competing Together.</div>
            </div>
            <div className="w-10 wsbf-rule my-4" />
            <div className="font-heading leading-tight tracking-wide text-base text-slate-100 wsbf-slant-block">
              <div>Skill</div>
              <div>Strategy</div>
              <div>Competition</div>
              <div>Without Limits.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ UPCOMING COMPETITIONS ============ */}
      <section
        className="relative bg-[var(--wsbf-black-2)] border-t border-[rgba(212,175,55,0.18)]"
        data-testid="home-competitions"
      >
        <div className={`${SHELL} py-8 lg:py-14 grid lg:grid-cols-12 gap-10`}>
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.38em] font-bold mb-4 wsbf-gold-text">Upcoming Competitions</div>
            <h2 className="font-heading leading-[0.88] text-2xl sm:text-3xl lg:text-4xl" data-testid="competitions-title">
              <span className="wsbf-slant-block inline-block text-slate-50 wsbf-glow">A Global </span>
              <span className="wsbf-slant-block inline-block wsbf-gold-text wsbf-glow-strong">Calendar</span>
            </h2>
            <p className="mt-5 text-xs sm:text-sm uppercase tracking-[0.26em] text-slate-200/90 font-semibold">
              Nations. Clubs. Players. Champions.
            </p>
            <Link
              to="/competitions"
              data-testid="comp-view-all"
              className="group mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-sm text-xs font-bold uppercase tracking-wider text-slate-100 wsbf-foil hover:text-[var(--wsbf-gold-300)]"
              style={{ transition: "color 0.2s ease, box-shadow 0.2s ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 36px rgba(212,175,55,0.25)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; }}
            >
              View All Competitions
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1" style={{ transition: "transform 0.2s ease" }} />
            </Link>
          </div>

          <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 items-start">
            {COMPETITIONS.map((c, i) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`competition-${c.to.replace(/\//g, "")}`}
                className="group relative overflow-hidden rounded-lg animate-fade-up wsbf-foil"
                style={{ aspectRatio: c.ratio, animationDelay: `${i * 70}ms`, transition: "box-shadow 0.25s ease, border-color 0.25s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 42px rgba(212,175,55,0.28)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; }}
              >
                <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105" style={{ transition: "transform 0.5s ease" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--wsbf-black)] via-[rgba(5,5,5,0.45)] to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 flex items-end justify-between gap-2">
                  <div>
                    <div className="font-heading text-2xl sm:text-3xl lg:text-2xl xl:text-xl 2xl:text-2xl tracking-tight text-slate-50 leading-none wsbf-slant-block wsbf-glow">
                      {c.title}
                    </div>
                    <div className="mt-2 text-[10px] sm:text-xs lg:text-[10px] 2xl:text-xs uppercase tracking-[0.24em] font-bold wsbf-gold-text">{c.sub}</div>
                  </div>
                  <span
                    className="shrink-0 w-9 h-9 rounded-full grid place-items-center text-slate-100 group-hover:text-[var(--wsbf-black)]"
                    style={{
                      border: "1px solid rgba(212,175,55,0.6)",
                      transition: "background 0.25s ease, color 0.25s ease, border-color 0.25s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--wsbf-grad-gold)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <section className="bg-[#000] border-t border-[rgba(255,215,0,0.35)]" data-testid="home-footer">
        <div className={`${SHELL} py-7 flex flex-col sm:flex-row items-center justify-between gap-6`}>
          <div className="flex items-center gap-4">
            <Shield className="w-7 h-7 text-[var(--wsbf-gold)]" style={{ filter: "drop-shadow(0 0 10px rgba(212,175,55,0.5))" }} />
            <div className="leading-none">
              <div className="font-heading text-xl tracking-widest text-slate-50 wsbf-slant-block">WSTF</div>
              <div className="text-[9px] font-bold uppercase tracking-[0.2em] mt-1 wsbf-gold-text">World Sports Trading Federation</div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-[rgba(212,175,55,0.25)] mx-2" />
            <div className="hidden sm:flex items-center gap-2 text-[11px] font-heading tracking-[0.22em] text-slate-300">
              <Globe2 className="w-4 h-4 text-[var(--wsbf-gold)]" /> Players. Nations. A Global Stage.
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-heading tracking-[0.22em] text-slate-300">
            <span>Skill</span><span>Strategy</span><span>Community</span><span>Without Limits.</span>
            <div className="w-8 wsbf-rule" />
            <span className="text-slate-50 font-heading wsbf-slant-block">WSTF</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function Counter({ Icon, value, label, testid }) {
  return (
    <div className="flex items-center gap-1.5 sm:gap-2.5 pr-1 sm:pr-2" data-testid={testid}>
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--wsbf-gold)] shrink-0" style={{ filter: "drop-shadow(0 0 8px rgba(212,175,55,0.45))" }} />
      <div>
        <div className="font-heading text-lg sm:text-3xl text-slate-50 tabular-nums leading-none wsbf-slant-block wsbf-glow">
          {value}
        </div>
        <div className="text-[7px] sm:text-[9px] uppercase tracking-[0.18em] text-slate-300/90 font-bold mt-0.5 sm:mt-1">{label}</div>
      </div>
    </div>
  );
}

function Divider() {
  return <div className="w-px h-6 sm:h-8 bg-[rgba(212,175,55,0.25)] mx-2 sm:mx-5" />;
}

const RANK_STYLES = [
  { color: "var(--wsbf-gold-300)", glow: "0 0 24px rgba(212,175,55,0.55)" }, // #1 gold
  { color: "#D7DBE1",              glow: "0 0 24px rgba(215,219,225,0.35)" }, // #2 silver
  { color: "#CB8E4E",              glow: "0 0 24px rgba(203,142,78,0.45)" }, // #3 bronze
];
const DEFAULT_RANK_STYLE = { color: "var(--wsbf-gold-300)", glow: "0 0 20px rgba(212,175,55,0.35)" };

// Fixed portrait↔rank pairing: rank #N always shows player-N.jpg.
function portraitFor(position) {
  return PORTRAITS[(position - 1 + PORTRAITS.length) % PORTRAITS.length];
}

// One full card visual (image + overlay) for a given rank holder.
function CardFace({ it }) {
  const e = it.entity || {};
  const style = RANK_STYLES[it.position - 1] || DEFAULT_RANK_STYLE;
  return (
    <div className="absolute inset-0">
      {/* Decorative cinematic athlete imagery (art direction only — does NOT depict the actual player). */}
      <img src={portraitFor(it.position)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105" style={{ transition: "transform 0.6s ease" }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--wsbf-black)] via-[rgba(5,5,5,0.4)] to-transparent" />
      <div className="absolute top-4 left-4 font-heading text-5xl sm:text-6xl wsbf-slant-block" style={{ color: style.color, textShadow: style.glow }}>
        #{it.position}
      </div>
      <div className="absolute top-5 right-4 text-[10px] font-mono uppercase tracking-[0.24em] text-slate-200/80">Player</div>
      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6">
        <div className="font-heading text-xl sm:text-2xl tracking-tight text-slate-50 truncate group-hover:wsbf-gold-text wsbf-slant-block wsbf-glow">
          <Flag country={e.country} width={20} className="mr-1.5 align-middle" />{e.display_name}
        </div>
        <div className="mt-2 flex items-center gap-1.5 font-mono text-base sm:text-lg font-bold wsbf-gold-text">
          <Trophy className="w-4 h-4 text-[var(--wsbf-gold)]" /> {Math.round(it.rating)}{" "}
          <span className="text-slate-300 text-[11px] font-sans uppercase tracking-wider">pts</span>
        </div>
      </div>
    </div>
  );
}

// One podium slot: crossfades (image + overlay together) whenever its rank holder changes.
function PlayerCard({ it, onPause }) {
  const [faceA, setFaceA] = useState(it);
  const [faceB, setFaceB] = useState(it);
  const [showA, setShowA] = useState(true);
  const showARef = useRef(true);
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (showARef.current) setFaceB(it); else setFaceA(it);
    showARef.current = !showARef.current;
    setShowA(showARef.current);
  }, [it]);

  return (
    <Link
      to={`/player/${(it.entity || {}).wsbf_player_id}`}
      data-testid={`podium-${it.position}`}
      className="group relative block overflow-hidden rounded-xl aspect-[5/6] bg-[var(--wsbf-black-2)] wsbf-foil"
      style={{ transition: "box-shadow 0.25s ease, border-color 0.25s ease" }}
      onMouseEnter={(ev) => { onPause(true); ev.currentTarget.style.boxShadow = "0 0 46px rgba(212,175,55,0.32)"; }}
      onMouseLeave={(ev) => { onPause(false); ev.currentTarget.style.boxShadow = ""; }}
    >
      <div className="absolute inset-0" style={{ opacity: showA ? 1 : 0, transition: `opacity ${ROTATE_FADE}ms ease` }}>
        <CardFace it={faceA} />
      </div>
      <div className="absolute inset-0" style={{ opacity: showA ? 0 : 1, transition: `opacity ${ROTATE_FADE}ms ease` }}>
        <CardFace it={faceB} />
      </div>
    </Link>
  );
}

// The triad: shows 3 consecutive ranks and crossfades ALL three together every
// ROTATE_INTERVAL, cycling through disjoint groups (ranks 1-3, 4-6, 7-9, 10-12 ...).
function TopPlayersTriad({ players }) {
  const groups = [];
  for (let g = 0; (g + 1) * 3 <= players.length; g++) {
    groups.push(players.slice(g * 3, g * 3 + 3));
  }
  // Fallback for incomplete data: show whatever we have, no rotation.
  const cycle = groups.length ? groups : [players];

  const [group, setGroup] = useState(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    if (cycle.length <= 1) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setGroup((g) => (g + 1) % cycle.length);
    }, ROTATE_INTERVAL);
    return () => clearInterval(id);
  }, [cycle.length]);

  const current = cycle[Math.min(group, cycle.length - 1)] || [];

  // Disjoint groups guarantee no portrait repeats in the visible triad — assert it.
  if (process.env.NODE_ENV !== "production") {
    const used = current.map((it) => portraitFor(it.position));
    console.assert(new Set(used).size === used.length, "WSTF: duplicate portrait in visible triad", used);
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="top-players-triad">
      {current.map((it, slot) => (
        <PlayerCard key={slot} it={it} onPause={(v) => { pausedRef.current = v; }} />
      ))}
    </div>
  );
}
